import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useEffect, useMemo, useRef, useState } from "react";

import { ZoomMode } from "../../../types/zoom_mode";
import { RangeMode } from "../bookings_view/bookings_view";
import { getModeDays } from "../calendar/calendar.utils";

const DAY_WIDTH_LARGE = 42;
const DAY_WIDTH_MEDIUM = 26;
const DAY_WIDTH_SMALL = 14;

const calculateZoomMode = (dayWidth: number): ZoomMode => {
  if (dayWidth > DAY_WIDTH_LARGE) {
    return ZoomMode.Full;
  }
  if (dayWidth > DAY_WIDTH_MEDIUM) {
    return ZoomMode.Medium;
  }
  if (dayWidth > DAY_WIDTH_SMALL) {
    return ZoomMode.Small;
  }
  return ZoomMode.Tiny;
};

type UseCalendar = {
  rangeMode: RangeMode;
};

type UseCalendarReturn = {
  calendarRef: React.RefObject<HTMLDivElement>;
  dayWidth: number;
  zoomMode: ZoomMode;
};

export const useCalendar = ({ rangeMode }: UseCalendar): UseCalendarReturn => {
  const { utc } = useDateFormatter();
  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarWidth, setCalendarWidth] = useState(1);

  const dayWidth = useMemo(
    () => (calendarWidth || 1) / getModeDays(rangeMode, utc),
    [calendarWidth, rangeMode]
  );

  const zoomMode = useMemo(() => calculateZoomMode(dayWidth), [dayWidth]);

  useEffect(() => {
    window.addEventListener("resize", handleWidthChange);
    return () => {
      window.removeEventListener("resize", handleWidthChange);
    };
  }, []);

  useEffect(() => handleWidthChange(), [calendarRef, rangeMode]);

  const handleWidthChange = () => {
    if (calendarRef.current) {
      setCalendarWidth(calendarRef.current.offsetWidth);
    }
  };

  return {
    calendarRef,
    dayWidth,
    zoomMode
  };
};
